import { Heading, Progress } from '@chakra-ui/react';
import { IUsageEventResSchema } from '@monetize/types/app';
import { MimeTypeEnum } from '@monetize/types/common';
import { formatInteger, formatNumber, pluralize } from '@monetize/utils/core';
import { unparse } from 'papaparse';
import { FunctionComponent as FC, useEffect, useMemo, useState } from 'react';
import { FiDownload } from 'react-icons/fi';
import { MdWarning } from 'react-icons/md';
import {
  MAlert,
  MBox,
  MButton,
  MFlex,
  MIcon,
  MText,
} from '../../../../../components/Monetize';
import { createColumnHelper } from '../../../../../components/Monetize/DataTableNew/appTable';
import { DataTable } from '../../../../../components/Monetize/DataTableNew/DataTable';
import {
  AccountLookupPopover,
  SubscriptionLookupPopover,
  UsageTypeLookupPopover,
} from '../../../../../components/Monetize/LookupPopover';
import { useUsageEventCustomFields } from '../../../../../hooks/useUsageEventCustomFields';
import { useFlags } from '../../../../../services/launchDarkly';
import { saveFile } from '../../../../../utils/download';
import { collectPropertyKeys } from '../usageProperties.utils';
import { getUsagePropertyColumns } from '../usagePropertyColumns';
import { PROPERTIES_COLUMN_PREFIX } from './processUsageCsvParse';

const MAX_VISIBLE_ERRORS = 1000;

const CSV_FIELDS = [
  'error',
  'id',
  'accountId',
  'subscriptionId',
  'usageTypeId',
  'unitsConsumed',
  'timestamp',
  'ratedPrice',
];

const columnHelper =
  createColumnHelper<IUsageEventResSchema['failedEvents'][number]>();

const baseColumns = [
  columnHelper.accessor('reason', {
    header: 'Error Message',
    cell: (info) => info.getValue(),
    size: 250,
  }),
  columnHelper.accessor('event.id', {
    header: 'Id',
    cell: (info) => info.getValue(),
  }),
  columnHelper.accessor('event.timestamp', {
    header: 'Timestamp',
    cell: (info) => info.getValue(),
  }),
  columnHelper.accessor('event.accountId', {
    header: 'Account Id',
    cell: (info) => <AccountLookupPopover value={info.getValue()} />,
    size: 180,
    minSize: 180,
  }),
  columnHelper.accessor('event.subscriptionId', {
    header: 'Subscription Id',
    cell: (info) => (
      <SubscriptionLookupPopover
        value={info.getValue()}
        record={info.row.original.event}
      />
    ),
    size: 180,
    minSize: 180,
  }),
  columnHelper.accessor('event.usageTypeId', {
    header: 'Usage Type Id',
    cell: (info) => <UsageTypeLookupPopover value={info.getValue()} />,
    size: 180,
    minSize: 180,
  }),
  columnHelper.accessor('event.unitsConsumed', {
    header: 'Units Consumed',
    cell: (info) => info.getValue(),
    size: 180,
    minSize: 180,
  }),
  columnHelper.accessor('event.ratedPrice', {
    header: 'Rated Price',
    cell: (info) => info.getValue(),
    enableResizing: false,
  }),
];

function getRowCountText(rowCount: number): string {
  if (rowCount > MAX_VISIBLE_ERRORS) {
    return `Previewing the first ${formatInteger(MAX_VISIBLE_ERRORS)} of ${formatInteger(
      rowCount,
    )} rows`;
  }
  return `${formatInteger(rowCount)} ${pluralize('row', rowCount)}`;
}

interface UploadUsageProgressProps {
  finished: boolean;
  successCount: number;
  failureCount: number;
  total: number;
  errors: IUsageEventResSchema['failedEvents'];
}

export const UploadUsageProgress: FC<UploadUsageProgressProps> = ({
  finished,
  successCount,
  failureCount,
  total,
  errors,
}: UploadUsageProgressProps) => {
  const [visibleErrors, setVisibleErrors] = useState(() =>
    errors.slice(0, MAX_VISIBLE_ERRORS),
  );
  const progress = (failureCount + successCount / total) * 100;
  const { enableUsageCustomFields } = useFlags();
  const { customFieldsByKey } = useUsageEventCustomFields();

  useEffect(() => {
    setVisibleErrors(errors.slice(0, MAX_VISIBLE_ERRORS));
  }, [errors]);

  // Keys come from the rejected events themselves: the download is meant to be
  // corrected and re-uploaded, so it has to carry back every property it sent.
  const propertyKeys = useMemo(
    () =>
      enableUsageCustomFields
        ? collectPropertyKeys(errors.map(({ event }) => event))
        : [],
    [enableUsageCustomFields, errors],
  );
  const propertyKeysSignature = propertyKeys.join('|');

  const columns = useMemo(
    () => [
      ...baseColumns,
      ...getUsagePropertyColumns(
        columnHelper,
        propertyKeys,
        customFieldsByKey,
        (row) => row.event.properties,
      ),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [propertyKeysSignature, customFieldsByKey],
  );

  function handleDownloadErrors() {
    const csv = unparse(
      {
        fields: [
          ...CSV_FIELDS,
          ...propertyKeys.map((key) => `${PROPERTIES_COLUMN_PREFIX}${key}`),
        ],
        data: errors.map(({ reason, event }) => ({
          error: reason,
          ...event,
          ...Object.fromEntries(
            propertyKeys.map((key) => [
              `${PROPERTIES_COLUMN_PREFIX}${key}`,
              event.properties?.[key] ?? '',
            ]),
          ),
        })),
      },
      { header: true },
    );
    saveFile(
      csv,
      `usage-import-errors-${new Date().getTime()}.csv`,
      MimeTypeEnum.CSV,
    );
  }

  return (
    <>
      {!finished && (
        <>
          <Heading size="sm" mb={4}>
            Importing..
          </Heading>
          <Progress.Root colorPalette="tIndigo" size="sm" value={progress}>
            <Progress.Track>
              <Progress.Range />
            </Progress.Track>
          </Progress.Root>
          <MText mt={4} fontStyle="italic">
            {successCount} of{' '}
            {formatNumber(total, {
              maximumFractionDigits: 0,
            })}{' '}
            rows imported
          </MText>
          {/* Results */}
          {!!failureCount && (
            <MFlex alignItems="center">
              <MIcon
                as={MdWarning}
                w={4}
                h={4}
                mr={2}
                color="tOrange.tangerine"
              />
              {formatNumber(total, {
                maximumFractionDigits: 0,
              })}{' '}
              import errors
            </MFlex>
          )}
        </>
      )}
      {finished && (
        // eslint-disable-next-line react/jsx-no-useless-fragment
        <>
          {failureCount ? (
            <MFlex flexDirection="column">
              <MAlert type="warning">
                {formatInteger(failureCount)} records were not successfully
                uploaded. Fix the errors by downloading the file below and try
                again.
              </MAlert>
              <MText my={4} fontStyle="italic">
                {formatInteger(successCount)} out of {formatInteger(total)}{' '}
                {pluralize('row', total)} successfully imported
              </MText>
              <MBox>
                <MButton
                  variant="tertiary"
                  size="sm"
                  ml={-3}
                  mt={4}
                  onClick={handleDownloadErrors}
                >
                  <MIcon as={FiDownload} mr={2} />
                  Download Errors
                </MButton>
              </MBox>
              <MText mb={2}>{getRowCountText(visibleErrors.length)}</MText>
              <DataTable
                enablePagination={false}
                columns={columns}
                data={visibleErrors}
                clientControlled
              />
            </MFlex>
          ) : (
            <MFlex flexDirection="column">
              <MAlert type="success">
                Your usage file has been imported successfully.
              </MAlert>
              <MText mt={4} fontStyle="italic">
                {formatInteger(successCount)} out of {formatInteger(total)}{' '}
                {pluralize('row', total)} successfully imported
              </MText>
            </MFlex>
          )}
        </>
      )}
    </>
  );
};
